import { useEffect, useRef, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form } from "react-bootstrap";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import { canAccessAllPlatforms } from "@serie3/common/config/RoleRights";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlugCircleBolt } from "@fortawesome/free-solid-svg-icons";
import {
  faLinkedin, faMeta, faYoutube, faTiktok,
  faXTwitter, faDropbox, faGoogleDrive, faMicrosoft,
} from "@fortawesome/free-brands-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import getLinkedinSettings from "@serie3/mymediaplanner/Config/Social/linkedinSettings";
import getYoutubeSettings from "@serie3/mymediaplanner/Config/Social/youtubeSettings";
import getMetaSettings from "@serie3/mymediaplanner/Config/Social/metaSettings";
import getTiktokSettings from "@serie3/mymediaplanner/Config/Social/tiktokSettings";
import generateRandomString from "@serie3/mymediaplanner/Domain/generateRandom";
import ToastMessages from "../../../../ToastMessages";
import {
  ConnectCard, StatusBadge, ComingSoonBadge,
  SectionHeader, ConnectionGrid,
} from "./ConnectCard";

/**
 * Single page that renders all connection cards.
 * Pass `callbackPlatform` ("linkedin" | "youtube" | "meta" | "tiktok") when landing
 * from an OAuth redirect so the code is exchanged with the backend.
 */
const ConnectionsPage = ({ callbackPlatform = null }) => {
  const { t } = useTranslation();
  const { settings } = useContext(SettingsContext);
  const allPlatforms = canAccessAllPlatforms(settings?.user?.roles);
  const linkedinSettings = getLinkedinSettings();
  const youtubeSettings = getYoutubeSettings();
  const metaSettings = getMetaSettings();
  const tiktokSettings = getTiktokSettings();
  const [linkedinConnected, setLinkedinConnected] = useState(false);
  const [youtubeConnected, setYoutubeConnected] = useState(false);
  const [metaConnected, setMetaConnected] = useState(false);
  const [tiktokConnected, setTiktokConnected] = useState(false);
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [switchingMetaAccount, setSwitchingMetaAccount] = useState(false);
  const [loading, setLoading] = useState(true);
  // OAuth codes are single-use. React StrictMode mounts twice in dev, which
  // would exchange the same code twice and fail the second time.
  const codeExchanged = useRef(false);

  // ── auth URL builders ────────────────────────────────────────────────────────

  const getLinkedinAuthUrl = () => {
    const statusCode = generateRandomString();
    localStorage.setItem("code", statusCode);
    return (
      linkedinSettings.linkeindAuthApiUrl +
      "?response_type=" + linkedinSettings.response_type +
      "&state=" + statusCode +
      "&client_id=" + linkedinSettings.client_id +
      "&redirect_uri=" + linkedinSettings.redirect_uri +
      "&scope=" + linkedinSettings.scope
    );
  };

  const getYoutubeAuthUrl = () =>
    youtubeSettings.youtubeAuthApiUrl +
    "?response_type=" + youtubeSettings.response_type +
    "&client_id=" + youtubeSettings.client_id +
    "&redirect_uri=" + encodeURIComponent(youtubeSettings.redirect_uri) +
    "&scope=" + encodeURIComponent(youtubeSettings.scope) +
    "&access_type=" + youtubeSettings.access_type +
    "&prompt=" + youtubeSettings.prompt;

  const getMetaAuthUrl = () =>
    metaSettings.metaAuthApiUrl +
    "?client_id=" + metaSettings.client_id +
    "&redirect_uri=" + encodeURIComponent(metaSettings.redirect_uri) +
    "&scope=" + encodeURIComponent(metaSettings.scope) +
    "&response_type=" + metaSettings.response_type +
    "&auth_type=" + metaSettings.auth_type;

  const getTiktokAuthUrl = () =>
    tiktokSettings.tiktokAuthApiUrl +
    "?client_key=" + tiktokSettings.clientKey +
    "&redirect_uri=" + encodeURIComponent(tiktokSettings.redirectUri) +
    "&scope=" + encodeURIComponent(tiktokSettings.scope) +
    "&response_type=" + tiktokSettings.response_type;

  // ── meta account selection ───────────────────────────────────────────────────

  const loadMetaAccounts = async () => {
    const response = await crudApi.justGet(SocialEndpoints.meta.accounts);
    if (response.ok) setMetaAccounts(response.data.accounts ?? []);
  };

  const activeMetaAccountId = metaAccounts.find((account) => account.active)?.igUserId ?? "";

  const metaAccountLabel = (account) => {
    const handle = account.igUsername ? `@${account.igUsername}` : account.igUserId;
    return account.pageName ? `${handle} — ${account.pageName}` : handle;
  };

  const changeMetaAccount = async (igUserId) => {
    if (!igUserId || igUserId === activeMetaAccountId) return;
    setSwitchingMetaAccount(true);
    const response = await crudApi.createNew(SocialEndpoints.meta.setActiveAccount, { igUserId });
    if (response.status === 200) {
      setMetaAccounts((accounts) =>
        accounts.map((account) => ({ ...account, active: account.igUserId === igUserId }))
      );
    }
    setSwitchingMetaAccount(false);
  };

  const disconnectMeta = async () => {
    const response = await crudApi.justGet(SocialEndpoints.meta.disconnect);
    response.ok ? ToastMessages.SuccessfulSave() : ToastMessages.Error();
    if (response.ok) {
      setMetaConnected(false);
      setMetaAccounts([]);
    }
  };

  // ── handle OAuth callback ────────────────────────────────────────────────────

  useEffect(() => {
    const init = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const error = params.get("error");

      if (error) {
        console.error("[ConnectionsPage] OAuth error:", error, params.get("error_description"));
      }

      console.log("[ConnectionsPage] callbackPlatform:", callbackPlatform);
      console.log("[ConnectionsPage] code:", code ? code.substring(0, 20) + "..." : null);

      const connectEndpoint = SocialEndpoints[callbackPlatform]?.connect;
      if (code && connectEndpoint && !codeExchanged.current) {
        codeExchanged.current = true;
        const response = await crudApi.createNew(connectEndpoint, { code }, null, true);
        if (response.status === 200) {
          ToastMessages.SuccessfulSave();
        } else {
          // The response carries debugging detail (provider answer, hint) meant for
          // us, not for the user — it goes to the console, the toast stays generic.
          console.error("[ConnectionsPage] connect failed:", callbackPlatform, response.status, response.data);
          const reference = response.data?.reference;
          ToastMessages.Error(
            reference
              ? t("Backend.Connections.ConnectFailedRef", "Connecting the account failed. Please try again. (Reference: {{reference}})", { reference })
              : t("Backend.Connections.ConnectFailed", "Connecting the account failed. Please try again.")
          );
        }
      }

      const [liStatus, ytStatus, igStatus, ttStatus] = await Promise.all([
        crudApi.justGet(SocialEndpoints.linkedin.isConnected),
        crudApi.justGet(SocialEndpoints.youtube.isConnected),
        crudApi.justGet(SocialEndpoints.meta.isConnected),
        crudApi.justGet(SocialEndpoints.tiktok.isConnected),
      ]);

      if (liStatus.ok) setLinkedinConnected(liStatus.data.connected);
      if (ytStatus.ok) setYoutubeConnected(ytStatus.data.connected);
      if (igStatus.ok) setMetaConnected(igStatus.data.connected);
      if (ttStatus.ok) setTiktokConnected(ttStatus.data.connected);

      if (igStatus.ok && igStatus.data.connected) {
        await loadMetaAccounts();
      }

      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── disconnect handlers ──────────────────────────────────────────────────────

  const disconnect = async (endpoint, setter) => {
    const response = await crudApi.justGet(endpoint);
    response.ok ? ToastMessages.SuccessfulSave() : ToastMessages.Error();
    if (response.ok) setter(false);
  };

  // ── helpers ──────────────────────────────────────────────────────────────────

  const comingSoonAction = (
    <Button variant="outline-secondary" size="sm" disabled
      style={{ borderRadius: 8, width: "100%", fontWeight: 600 }}>
      {t("Backend.Connections.NotAvailableYet")}
    </Button>
  );

  const connectAction = (href, label, style = {}) => (
    <a className="btn btn-sm"
      href={href}
      style={{ borderRadius: 8, width: "100%", fontWeight: 600, textAlign: "center", ...style }}>
      {label}
    </a>
  );

  const disconnectAction = (onDisconnect) => (
    <Button variant="outline-danger" size="sm"
      style={{ borderRadius: 8, width: "100%", fontWeight: 600 }}
      onClick={onDisconnect}>
      {t("Backend.Connections.Disconnect")}
    </Button>
  );

  const skeleton = <div className="skeleton" style={{ height: 32, borderRadius: 8 }} />;

  const metaAccountPicker = metaAccounts.length > 0 ? (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.6px",
        textTransform: "uppercase", color: "#9299a8", marginBottom: 6,
      }}>
        {t("Backend.Connections.Meta.PublishTo", "Publish to")}
      </label>
      <Form.Select
        size="sm"
        value={activeMetaAccountId}
        disabled={switchingMetaAccount}
        onChange={(e) => changeMetaAccount(e.target.value)}
        style={{ borderRadius: 8, fontSize: 13 }}
      >
        {metaAccounts.map((account) => (
          <option key={account.igUserId} value={account.igUserId}>
            {metaAccountLabel(account)}
          </option>
        ))}
      </Form.Select>
      <a
        href={getMetaAuthUrl()}
        style={{ display: "inline-block", fontSize: 11, fontWeight: 600, color: "#0081fb", marginTop: 8 }}
      >
        {t("Backend.Connections.Meta.RefreshAccounts", "Add or refresh accounts")}
      </a>
      <div style={{ fontSize: 11, color: "#9299a8", marginTop: 4, lineHeight: 1.5 }}>
        {t(
          "Backend.Connections.Meta.RefreshAccountsHint",
          "Missing an account? It must be an Instagram Business or Creator account linked to a Facebook Page you administer. If Meta skips the page-selection step, remove this app under Facebook → Settings → Apps and Websites first."
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <h4 className="page-section-header">
        <span className="page-section-header-icon" style={{ background: "rgba(3,142,220,0.12)" }}>
          <FontAwesomeIcon icon={faPlugCircleBolt} style={{ color: "#038edc" }} />
        </span>
        {t("Backend.Connections.Title")}
      </h4>

      {/* ── Social ── */}
      <SectionHeader label={t("Backend.Connections.Groups.Social")} />
      <ConnectionGrid>
        {/* LinkedIn */}
        <ConnectCard
          name="LinkedIn"
          icon={faLinkedin}
          color="#0077b5"
          description={t("Backend.Connections.LinkedIn.Description")}
          badge={<StatusBadge connected={linkedinConnected} loading={loading} />}
          action={loading ? skeleton : linkedinConnected
            ? disconnectAction(() => disconnect(SocialEndpoints.linkedin.disconnect, setLinkedinConnected))
            : connectAction(getLinkedinAuthUrl(), t("Backend.Connections.LinkedIn.Connect"), { color: "#0077b5", border: "2px solid #0077b5", background: "#fff" })
          }
        />

        {/* YouTube */}
        <ConnectCard
          name="YouTube"
          icon={faYoutube}
          color="#ff0000"
          description={t("Backend.Connections.YouTube.Description")}
          badge={allPlatforms ? <StatusBadge connected={youtubeConnected} loading={loading} /> : <ComingSoonBadge />}
          action={allPlatforms
            ? (loading ? skeleton : youtubeConnected
                ? disconnectAction(() => disconnect(SocialEndpoints.youtube.disconnect, setYoutubeConnected))
                : connectAction(getYoutubeAuthUrl(), t("Backend.Connections.YouTube.Connect", "Connect YouTube"), { background: "#ff0000", color: "#fff" }))
            : comingSoonAction}
          disabled={!allPlatforms}
        />

        {/* Meta (Instagram + Facebook) */}
        <ConnectCard
          name="Meta"
          icon={faMeta}
          color="#0081fb"
          description={t("Backend.Connections.Meta.Description", "Connect your Instagram & Facebook accounts via Meta")}
          badge={allPlatforms ? <StatusBadge connected={metaConnected} loading={loading} /> : <ComingSoonBadge />}
          extra={allPlatforms && !loading && metaConnected ? metaAccountPicker : null}
          action={allPlatforms
            ? (loading ? skeleton : metaConnected
                ? disconnectAction(disconnectMeta)
                : connectAction(getMetaAuthUrl(), t("Backend.Connections.Meta.Connect", "Connect Meta"), { background: "#0081fb", color: "#fff" }))
            : comingSoonAction}
          disabled={!allPlatforms}
        />

        {/* TikTok */}
        <ConnectCard
          name="TikTok"
          icon={faTiktok}
          color="#010101"
          description={t("Backend.Connections.TikTok.Description")}
          badge={allPlatforms ? <StatusBadge connected={tiktokConnected} loading={loading} /> : <ComingSoonBadge />}
          action={allPlatforms
            ? (loading ? skeleton : tiktokConnected
                ? disconnectAction(() => disconnect(SocialEndpoints.tiktok.disconnect, setTiktokConnected))
                : connectAction(getTiktokAuthUrl(), t("Backend.Connections.TikTok.Connect", "Connect TikTok"), { background: "#010101", color: "#fff" }))
            : comingSoonAction}
          disabled={!allPlatforms}
        />

        {/* coming soon */}
        <ConnectCard name="X (Twitter)" icon={faXTwitter} color="#000000"
          description={t("Backend.Connections.XTwitter.Description")}
          badge={<ComingSoonBadge />} action={comingSoonAction} disabled />
      </ConnectionGrid>

      {/* ── Storage ── */}
      <SectionHeader label={t("Backend.Connections.Groups.Storage")} />
      <ConnectionGrid>
        <ConnectCard name="Google Drive" icon={faGoogleDrive} color="#4285f4"
          description={t("Backend.Connections.GoogleDrive.Description")}
          badge={<ComingSoonBadge />} action={comingSoonAction} disabled />
        <ConnectCard name="Dropbox" icon={faDropbox} color="#0061ff"
          description={t("Backend.Connections.Dropbox.Description")}
          badge={<ComingSoonBadge />} action={comingSoonAction} disabled />
        <ConnectCard name="OneDrive" icon={faMicrosoft} color="#0078d4"
          description={t("Backend.Connections.OneDrive.Description")}
          badge={<ComingSoonBadge />} action={comingSoonAction} disabled />
        <ConnectCard name="Box" icon={null} color="#0061d5"
          description={t("Backend.Connections.Box.Description")}
          badge={<ComingSoonBadge />} action={comingSoonAction} disabled />
      </ConnectionGrid>

      {/* ── Creative ── */}
      <SectionHeader label={t("Backend.Connections.Groups.Creative")} />
      <ConnectionGrid>
        <ConnectCard name="Canva" icon={null} color="#00c4cc"
          description={t("Backend.Connections.Canva.Description")}
          badge={<ComingSoonBadge />} action={comingSoonAction} disabled />
      </ConnectionGrid>
    </>
  );
};

export default ConnectionsPage;
